/**
 * @fileoverview Per-customer kids store cart (Pinia) — separate from IPA store cart.
 *
 * Persistence: `localStorage` key `kids_cart_${userId}`.
 */

import { defineStore } from "pinia";
import { useAuthStore } from "../authen/auth";
import { ensureItemSizeSlots } from "../utils/orderSizes";

function unitPrice(item) {
  const p = Number(item?.price);
  return Number.isFinite(p) ? p : 0;
}

export const useKidsCartStore = defineStore("kidsCart", {
  state: () => ({
    items: [],
    userId: null,
    storagePrefix: "kids_cart_",
  }),

  getters: {
    cartCount: (state) =>
      state.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),

    cartTotal: (state) =>
      state.items.reduce(
        (sum, item) => sum + unitPrice(item) * (Number(item.quantity) || 0),
        0,
      ),

    isEmpty: (state) => state.items.length === 0,
  },

  actions: {
    _customerId(auth) {
      return auth?.user?.id ?? null;
    },

    _persistItemsForUserId(uid) {
      if (uid == null) return;
      try {
        localStorage.setItem(this.storagePrefix + uid, JSON.stringify(this.items));
      } catch (e) {
        console.warn("[kidsCart] Failed to persist cart:", e);
      }
    },

    _loadItemsForUserId(uid) {
      if (uid == null) {
        this.items = [];
        return;
      }
      try {
        const raw = localStorage.getItem(this.storagePrefix + uid);
        if (!raw) {
          this.items = [];
          return;
        }
        const parsed = JSON.parse(raw);
        this.items = Array.isArray(parsed) ? parsed : [];
        this.items.forEach((item) => ensureItemSizeSlots(item));
      } catch {
        this.items = [];
      }
    },

    validateAuth() {
      const auth = useAuthStore();
      if (!auth.isAuthenticated || !auth.user) {
        if (this.items.length > 0 || this.userId != null) {
          this.clearSessionCart();
        }
        return false;
      }

      const currentUserId = this._customerId(auth);
      if (this.userId != null && this.userId !== currentUserId) {
        this._persistItemsForUserId(this.userId);
        this.userId = currentUserId;
        this._loadItemsForUserId(this.userId);
        return true;
      }

      if (this.userId == null) {
        this.userId = currentUserId;
        this._loadItemsForUserId(this.userId);
      }
      return true;
    },

    initCart() {
      const auth = useAuthStore();
      if (!auth.isAuthenticated || !auth.user) {
        this.clearSessionCart();
        return;
      }
      this.userId = this._customerId(auth);
      this._loadItemsForUserId(this.userId);
    },

    handleLoginSession() {
      const auth = useAuthStore();
      if (!auth.isAuthenticated || !auth.user) {
        this.clearSessionCart();
        return;
      }
      const newUserId = this._customerId(auth);
      if (this.userId != null && this.userId !== newUserId) {
        this._persistItemsForUserId(this.userId);
      }
      this.userId = newUserId;
      this._loadItemsForUserId(this.userId);
      this.saveCart();
    },

    saveCart() {
      const auth = useAuthStore();
      if (!auth.isAuthenticated || this.userId == null) {
        if (this.items.length > 0) this.clearSessionCart();
        return;
      }
      if (auth.user && auth.user.id !== this.userId) return;
      this._persistItemsForUserId(this.userId);
    },

    clearSessionCart() {
      this.items = [];
      this.userId = null;
    },

    logout() {
      this.clearSessionCart();
    },

    syncWithAuth() {
      return this.validateAuth();
    },

    addToCart(product, quantity = 1) {
      if (!this.validateAuth()) {
        throw new Error("Please log in to add items to your cart");
      }

      const qty = Math.max(1, Number(quantity) || 1);
      const existing = this.items.find((i) => i.item_id === product.item_id);

      if (existing) {
        existing.quantity = (Number(existing.quantity) || 0) + qty;
        ensureItemSizeSlots(existing);
      } else {
        this.items.push({
          item_id: product.item_id,
          name: product.name,
          price: product.price,
          thumbnail: product.thumbnail || product.image_url || null,
          quantity: qty,
          currency: product.currency || "KES",
          sub_category: product.sub_category || null,
          slug: product.slug || null,
          sizes: [],
        });
        ensureItemSizeSlots(this.items[this.items.length - 1]);
      }
      this.saveCart();
    },

    increaseQty(product) {
      if (!this.validateAuth()) return;
      const item = this.items.find((i) => i.item_id === product.item_id);
      if (item) {
        item.quantity = (Number(item.quantity) || 0) + 1;
        ensureItemSizeSlots(item);
        this.saveCart();
      }
    },

    decreaseQty(product) {
      if (!this.validateAuth()) return;
      const item = this.items.find((i) => i.item_id === product.item_id);
      if (!item) return;
      const q = Number(item.quantity) || 0;
      if (q > 1) {
        item.quantity = q - 1;
        ensureItemSizeSlots(item);
      } else {
        this.items = this.items.filter((i) => i.item_id !== product.item_id);
      }
      this.saveCart();
    },

    removeFromCart(productId) {
      if (!this.validateAuth()) return;
      this.items = this.items.filter((i) => i.item_id !== productId);
      this.saveCart();
    },

    clearCart() {
      if (!this.validateAuth()) return;
      this.items = [];
      this.saveCart();
    },

    updateQuantity(productId, quantity) {
      if (!this.validateAuth()) return;
      const item = this.items.find((i) => i.item_id === productId);
      if (!item) return;
      const q = Number(quantity);
      if (q > 0) {
        item.quantity = q;
        ensureItemSizeSlots(item);
        this.saveCart();
      } else {
        this.removeFromCart(productId);
      }
    },

    setItemSize(productId, unitIndex, sizeValue) {
      if (!this.validateAuth()) return;
      const item = this.items.find((i) => i.item_id === productId);
      if (!item) return;
      ensureItemSizeSlots(item);
      if (unitIndex < 0 || unitIndex >= item.sizes.length) return;
      item.sizes[unitIndex] = sizeValue;
      this.saveCart();
    },

    syncAllItemSizeSlots() {
      this.items.forEach((item) => ensureItemSizeSlots(item));
    },

    getItemPrice(item) {
      return unitPrice(item);
    },

    getItemTotal(item) {
      return unitPrice(item) * (Number(item.quantity) || 0);
    },
  },
});
